<template>
    <div class="network-credential-import" :class="{loading: loading}">
        <div v-if="project_id && file">
            <form @submit.prevent="openExecuteModal" method="POST"
                  accept-charset="UTF-8"
                  data-vv-scope="execute"
                  novalidate="novalidate">

                <div class="row" v-if="project">
                    <div class="col-sm-6 col-md-4 form-group ">
                        <div class="has-feedback"
                             v-if="project.network_type === 0"
                             :class="{'has-error': errors.has('execute.ssid_id')}">
                            <label for="ssid-id">SSID</label>

                            <select id="ssid-id"
                                    v-validate="'required'"
                                    v-model="ssid_id"
                                    name="ssid_id"
                                    class="form-control"
                                    aria-required="true">
                                <option value="">-</option>
                                <option v-for="ssid in ssids" :value="ssid.id">
                                    {{ssid.name}}
                                </option>
                            </select>

                            <span v-show="errors.has('execute.ssid_id')" class="help-block">
                            {{ errors.first('execute.ssid_id') }}
                        </span>
                        </div>
                    </div>

                    <div class="col-sm-6 col-md-8 form-group text-right">
                        <label>&nbsp;</label>

                        <button v-if="canExecute" type="button"
                                class="btn btn-warning"
                                @click="openExecuteModal"
                                :disabled="loading">
                            Execute
                        </button>

                        <dropdown menu-right>
                            <btn class="dropdown-toggle" type="primary">
                                <i class="fa fa-cog" aria-hidden="true"></i>
                            </btn>

                            <template slot="dropdown">
                                <li>
                                    <a role="button" @click="openModal" :disabled="loading">
                                        Load Import File
                                    </a>
                                </li>
                                <li class="checkbox">
                                    <label>
                                        <input type="checkbox" value="true" v-model="updateOnly"> Update Only
                                    </label>
                                </li>
                            </template>
                        </dropdown>
                    </div>
                </div>

                <table class="table table-responsive">
                    <colgroup>
                        <col v-for="(heading, index) in headings"
                             :class="[typeof heading.value === 'string' ? getColumnClass(index) : '']">
                    </colgroup>
                    <thead>
                    <tr>
                        <th v-for="(heading, index) in headings">
                            <select class="form-control margin-md-bottom"
                                    :data-index="index"
                                    v-model="heading.value"
                                    @change="onHeadingChange">
                                <option v-for="( value, label ) in headingOptions" :value="value">
                                    {{label}}
                                </option>
                            </select>
                        </th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="row in data">
                        <td v-for="cellValue in row">
                            {{cellValue}}
                        </td>
                    </tr>
                    </tbody>
                </table>
            </form>
        </div>

        <div v-else class="panel panel-default">
            <div class="panel-body text-center padding-sm-top background-white">
                <div class="form-group">
                    <button class="btn btn-warning" type="button" @click="openModal" :disabled="loading">
                        Set Import Options
                    </button>
                </div>
            </div>
        </div>

        <modal :name=modalId
               :adaptive="true"
               :scrollable="true"
               :clickToClose="false"
               height="auto">
            <div class="dashboard-block" :class="{loading: loading}">
                <form @submit.prevent="setOptions" method="POST"
                      accept-charset="UTF-8"
                      class="margin-md-top"
                      data-vv-scope="workbook"
                      novalidate="novalidate">
                    <fieldset>
                        <legend>Import Options</legend>
                    </fieldset>

                    <div class="form-group has-feedback"
                         :class="{'has-error': errors.has('workbook.project_id')}">
                        <label for="project-id">Project</label>

                        <select id="project-id"
                                v-model="project_id"
                                name="project_id"
                                class="form-control"
                                v-validate="'required'"
                                aria-required="true"
                                required="true">
                            <option value="">-</option>
                            <option v-for="project in projects" :value="project.id">
                                {{project.name}}
                            </option>
                        </select>

                        <span v-show="errors.has('workbook.project_id')" class="help-block">
                            {{ errors.first('workbook.project_id') }}
                        </span>
                    </div>

                    <div class="form-group has-feedback"
                         :class="{'has-error': errors.has('workbook.NetworkCredential[file]')}">
                        <input v-validate="'required'"
                               aria-required="true"
                               required="true"
                               id="network-credential-file"
                               name="NetworkCredential[file]"
                               type="file"
                               class="form-control dropable"
                               @change="onFileChange">

                        <span v-show="errors.has('workbook.NetworkCredential[file]')" class="help-block">
                            {{ errors.first('workbook.NetworkCredential[file]') }}
                        </span>
                    </div>

                    <hr>

                    <div class="row">
                        <div class="col-sm-6 col-ld-4 form-group">
                            <a class="btn btn-link" :disabled="loading" @click="closeModal">
                                Close
                            </a>
                        </div>

                        <div class="col-sm-6 col-ld-4 form-group pull-right">
                            <button type="submit" class="btn btn-success btn-block" :disabled="loading">
                                Load Data
                            </button>
                        </div>
                    </div>
                </form>
            </div>
        </modal>

        <modal name="confirm-execute" :adaptive="true" :scrollable="true" height="auto">
            <div class="dashboard-block">
                <fieldset>
                    <legend>Confirm Import</legend>

                    <h4 v-if="importData">
                        <span v-if="updateOnly">Updating {{importData.length}} Network Credentials</span>
                        <span v-else>Creating {{importData.length}} Network Credentials</span>
                    </h4>
                    <h6 v-if="project_id && project.name" class="text-bold">{{project.name}}</h6>

                    <hr>

                    <div class="row">
                        <div class="col-xs-6">
                            <button type="button" class="btn btn-default" @click="$modal.hide('confirm-execute')">
                                Cancel
                            </button>
                        </div>
                        <div class="col-xs-6 text-right">
                            <button role="button" @click="executeImport" :disabled="loading" class="btn btn-success btn-block">
                                Execute Import
                            </button>
                        </div>
                    </div>
                </fieldset>
            </div>
        </modal>
    </div>
</template>

<script>
    var XLSX = require( 'xlsx' );

    export default {
        props: {
            modalId: {
                default(){
                    return Math.random().toString();
                },
                type: String
            },
        },

        data(){
            return {
                loading: true,
                file: null,
                data: [],
                headings: [],
                updateOnly: false,

                ssid_id: null,
                ssids: [],

                project_id: null,
                projects: [],
            };
        },

        created(){
            this.init();
        },

        computed: {
            importData(){
                return _.reduce( this.data, ( carry, rowData, rowIndex ) => {
                    var row = {};

                    _.each( this.headings, ( heading, headingIndex ) => {
                        if( !_.isEmpty( heading.value ) ){
                            row[ 'NetworkCredential[' + rowIndex + ']' + heading.value ] = rowData[ headingIndex ];
                        }
                    } );

                    carry.push( row );

                    return carry;
                }, [] );
            },

            importForm(){
                var form = document.createElement( 'form' );
                form.setAttribute( 'method', 'POST' );
                form.setAttribute( 'action', '/networkCredentials/import' );

                var hiddenField = document.createElement( 'input' );
                hiddenField.setAttribute( 'type', 'hidden' );
                hiddenField.setAttribute( 'name', '_method' );
                hiddenField.setAttribute( 'value', 'POST' );

                var token = $( 'meta[name="csrf-token"]' ).attr( 'content' );
                var tokenField = document.createElement( 'input' );
                tokenField.setAttribute( 'type', 'hidden' );
                tokenField.setAttribute( 'name', '_token' );
                tokenField.setAttribute( 'value', token );

                form.appendChild( tokenField );
                form.appendChild( hiddenField );

                if( this.project ){
                    var projectIdField = document.createElement( 'input' );
                    projectIdField.setAttribute( 'type', 'hidden' );
                    projectIdField.setAttribute( 'name', 'project_id' );
                    projectIdField.setAttribute( 'value', this.project_id );

                    form.appendChild( projectIdField );

                    if( this.project.network_type === 0 ){
                        var ssidField = document.createElement( 'input' );
                        ssidField.setAttribute( 'type', 'hidden' );
                        ssidField.setAttribute( 'name', 'ssid_id' );
                        ssidField.setAttribute( 'value', this.ssid_id );

                        form.appendChild( ssidField );
                    }
                }

                _.each( this.importData, ( importRow ) => {
                    _.each( importRow, ( value, key ) => {
                        var field = document.createElement( 'input' );
                        field.setAttribute( 'type', 'hidden' );
                        field.setAttribute( 'name', key );
                        field.setAttribute( 'value', value );
                        form.appendChild( field );
                    } );
                } );

                if( this.updateOnly ){
                    var updateOnlyField = document.createElement( 'input' );
                    updateOnlyField.setAttribute( 'type', 'hidden' );
                    updateOnlyField.setAttribute( 'name', 'updateOnly' );
                    updateOnlyField.setAttribute( 'value', '1' );

                    form.appendChild( updateOnlyField );
                }

                return form;
            },

            canExecute(){
                if( this.project ){
                    if( this.project.network_type === 0 ){
                        return this.ssid_id && _.isEmpty( this.missingHeadings );
                    }
                    else if( this.project.network_type === 1 ){
                        return _.isEmpty( this.missingHeadings );
                    }
                }

                return false;
            },

            missingHeadings(){
                return _.difference( this.requiredHeadingValues, _.map( this.headings, 'value' ) );
            },

            requiredHeadingValues(){
                var result = [];

                if( this.project ){
                    var hasSSID = _.find( this.headings, { value: '[SSID][name]' } ) || _.find( this.headings, { value: '[SSID][password]' } );

                    if( this.project.network_type === 0 ){
                        result = _.concat( result, [
                            '[password]',
                            '[username]',
                        ] );
                    }
                    else if( this.project.network_type === 1 && hasSSID ){
                        if( _.find( this.headings, { value: '[password]' } ) || _.find( this.headings, { value: '[username]' } ) ){
                            result = _.concat( result, [
                                '[password]',
                                '[username]',
                            ] );
                        }

                        result = _.concat( result, [
                            '[Address][address]',
                            '[Address][address_2]',
                            '[Address][city]',
                            '[Address][state]',
                            '[Address][postal_code]',
                            '[SSID][name]',
                            '[SSID][password]',
                        ] );
                    }
                }

                return result;
            },

            headingOptions(){
                if( this.project ){
                    if( this.project.network_type === 0 ){
                        return {
                            'None': null,
                            'Password': '[password]',
                            'Unit Code': '[unit]',
                            'Username': '[username]',
                        };
                    }
                    else if( this.project.network_type === 1 ){
                        return {
                            'None': null,
                            'Address': '[Address][address]',
                            'Apartment': '[Address][address_2]',
                            'City': '[Address][city]',
                            'State': '[Address][state]',
                            'Postal Code': '[Address][postal_code]',
                            'Username': '[username]',
                            'Password': '[password]',
                            'Unit Code': '[unit]',
                            'SSID Name': '[SSID][name]',
                            'SSID Password': '[SSID][password]',
                        };
                    }
                }

                return {};
            },

            project(){
                if( this.project_id ){
                    return _.find( this.projects, { id: this.project_id } );
                }
            },
        },
        watch: {
            project_id(){
                this.loading = true;

                this.loadSSIDs()
                    .then( () => {
                        if( this.ssids.length > 0 ){
                            if( this.ssid_id !== null && !_.includes( _.map( this.ssids, 'id' ), this.ssid_id ) ){
                                this.ssid_id = null;
                            }
                            else if( this.ssid_id == null && this.ssids.length === 1 ){
                                this.ssid_id = _.head( this.ssids ).id;
                            }
                        }
                    } )
                    .finally( () => {
                        this.loading = false;
                    } );
            }
        },
        methods: {
            init(){
                this.loading = true;

                return this.loadProjects()
                    .catch( this.$root.handleErrors )
                    .finally( () => {
                        this.openModal();
                        this.loading = false;
                    } );
            },

            loadProjects(){
                var url = '/api/projects';

                return axios.get( url )
                    .then( ( response ) => {
                        var projects = _.sortBy( response.data.Projects, 'name' );

                        this.$set( this, 'projects', projects );
                    } );
            },

            loadSSIDs(){
                var url = '/api/ssids';

                if( !this.project_id ){
                    this.$set( this, 'ssids', [] );

                    return Promise.resolve( {} );
                }

                return axios.get( url + '?project_id=' + this.project_id )
                    .then( ( response ) => {
                        var ssids = response.data.SSIDs;

                        this.$set( this, 'ssids', ssids );
                    } )
                    .catch( this.$root.handleErrors );
            },

            loadWorkbook(){
                var reader = new FileReader();

                reader.onload = ( e ) => {
                    var data = e.target.result;

                    this.parseWorkbook( XLSX.read( data, { type: 'array' } ) );
                };

                reader.readAsArrayBuffer( this.file );
            },

            parseWorkbook( workbook ){
                var sheetName = _.head( workbook.SheetNames );
                var sheet = workbook.Sheets[ sheetName ];

                // Note: this will index all row values by the first row of the spreadsheet
                var parsedSheet = XLSX.utils.sheet_to_json( sheet );

                this.data = _.reduce( parsedSheet, ( carry, row, rowIndex ) => {
                    carry[ rowIndex ] = _.values( row );

                    return carry;
                }, [] );

                var firstRow = _.first( this.data );
                if( Array.isArray( firstRow ) ){
                    this.headings = Array( firstRow.length );

                    for( var i = 0; i < firstRow.length; i++ ){
                        this.headings[ i ] = { value: null };
                    }
                }
            },

            onFileChange( e ){
                this.file = e.target.files[ 0 ];
            },

            setOptions(){
                return this.$validator.validateAll( 'workbook' )
                    .then( ( valid ) => {
                        if( valid ){
                            this.loadWorkbook();
                            this.closeModal();
                        }
                    } );
            },

            onHeadingChange( e ){
                var value = e.target.value;

                if( _.isEmpty( value ) ){
                    value = null;
                }

                if( typeof value === 'string' ){
                    this.cleanupHeadings( value );
                }

                this.$set( this.headings, $( e.target ).attr( 'data-index' ), { value: value } );
            },

            cleanupHeadings( value ){
                for( var i = 0; i < this.headings.length; i++ ){
                    if( this.headings[ i ].value === value ){
                        this.$set( this.headings, i, { value: null } );
                    }
                }
            },

            getColumnClass( index ){
                var classNames = [
                    'info',
                    'success',
                    'warning',
                    'danger',
                    'excellent',
                ];

                return classNames[ index % classNames.length ];
            },

            openExecuteModal(){
                return this.$validator.validateAll( 'execute' )
                    .then( ( valid ) => {
                        if( valid ){
                            this.$modal.show( 'confirm-execute' );
                        }
                    } );
            },

            executeImport(){
                return this.$validator.validateAll( 'execute' )
                    .then( ( valid ) => {
                        if( valid ){
                            this.$modal.hide( 'confirm-execute' );

                            this.loading = true;

                            const form = this.importForm;

                            document.body.appendChild( form );
                            form.submit();
                        }
                    } );
            },

            closeModal(){
                this.$modal.hide( this.modalId );
            },

            openModal(){
                this.$modal.show( this.modalId );
            },
        }
    }
</script>
